import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RefreshCw, Users, CheckCircle, Clock, AlertTriangle, Plus, Settings, Shield, Wrench, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminTradesServices() {
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('UK');
  const [countryConfig, setCountryConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [pendingProviders, setPendingProviders] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, statsRes, pendingRes, subcatRes] = await Promise.all([
        fetch(`${API_URL}/api/trades-services/config/country/${country}`),
        fetch(`${API_URL}/api/trades-services/admin/stats?country=${country}`),
        fetch(`${API_URL}/api/trades-services/admin/providers/pending?country=${country}`),
        fetch(`${API_URL}/api/trades-services/config/subcategories`),
      ]);

      const [configData, statsData, pendingData, subcatData] = await Promise.all([
        configRes.json(),
        statsRes.json(),
        pendingRes.json(),
        subcatRes.json(),
      ]);

      setCountryConfig(configData);
      setStats(statsData.stats);
      setPendingProviders(pendingData.pending_providers || []);
      setAllSubcategories(subcatData.subcategories || {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [country]);

  const handleVerification = async (providerId, decision, level = 1) => {
    try {
      await fetch(`${API_URL}/api/trades-services/admin/providers/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider_id: providerId,
          decision,
          verification_level: level,
        }),
      });
      fetchData();
    } catch (error) {
      console.error('Verification failed:', error);
    }
  };

  const getComplianceBadge = (level) => {
    const colors = {
      low: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-red-100 text-red-700',
    };
    return (
      <Badge className={colors[level] || colors.low}>
        {level?.toUpperCase() || 'LOW'}
      </Badge>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="w-6 h-6" />
            Services & Trades Administration
          </h1>
          <p className="text-gray-600 mt-1">
            Manage trade service providers, categories, and compliance
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Select Country" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
              <SelectItem value="LK">🇱🇰 Sri Lanka</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Providers</p>
                  <p className="text-2xl font-bold">{stats.total_providers}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Providers</p>
                  <p className="text-2xl font-bold text-green-600">{stats.active_providers}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Verification</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.pending_verifications}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Subcategories</p>
                  <p className="text-2xl font-bold">{countryConfig?.subcategories?.length || 0}</p>
                </div>
                <Settings className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="subcategories">Subcategories</TabsTrigger>
          <TabsTrigger value="verification">
            Verification
            {pendingProviders.length > 0 && (
              <Badge variant="destructive" className="ml-2">{pendingProviders.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="emergency">Emergency Services</TabsTrigger>
          <TabsTrigger value="settings">Country Settings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Providers by Subcategory */}
            <Card>
              <CardHeader>
                <CardTitle>Providers by Subcategory</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.by_subcategory && Object.keys(stats.by_subcategory).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(stats.by_subcategory).map(([slug, count]) => {
                      const subcat = allSubcategories[slug] || countryConfig?.subcategories?.find(s => s.slug === slug);
                      return (
                        <div key={slug} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{subcat?.subcategory_icon || '🔧'}</span>
                            <span className="font-medium">{subcat?.subcategory_name || slug}</span>
                          </div>
                          <Badge variant="secondary">{count} providers</Badge>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No providers yet</p>
                )}
              </CardContent>
            </Card>

            {/* Country Info */}
            <Card>
              <CardHeader>
                <CardTitle>Country Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                {countryConfig?.country && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{country === 'UK' ? '🇬🇧' : '🇱🇰'}</span>
                      <span className="text-xl font-semibold">{countryConfig.country.name}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Currency</p>
                        <p className="font-medium">{countryConfig.country.currency} ({countryConfig.country.currency_symbol})</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Subcategories</p>
                        <p className="font-medium">{countryConfig.subcategories?.length || 0}</p>
                      </div>
                    </div>
                    {country === 'LK' && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-700 flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Custom LK-only Subcategories
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {countryConfig.subcategories?.filter(s => s.is_custom).map(sub => (
                            <Badge key={sub.slug} variant="outline" className="border-blue-200">
                              {sub.subcategory_icon} {sub.subcategory_name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Subcategories Tab */}
        <TabsContent value="subcategories">
          <div className="space-y-4">
            {countryConfig?.subcategories?.map((subcat) => (
              <Card key={subcat.slug}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <span className="text-2xl">{subcat.subcategory_icon}</span>
                      {subcat.subcategory_name}
                      {subcat.is_custom && (
                        <Badge className="bg-blue-100 text-blue-700 ml-2">
                          {subcat.country_only}-only
                        </Badge>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getComplianceBadge(subcat.compliance_level)}
                      {subcat.licensed_trade && (
                        <Badge className="bg-purple-100 text-purple-700">
                          <Shield className="w-3 h-3 mr-1" />
                          Licensed Trade
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {subcat.disclaimer && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {subcat.disclaimer}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {Object.entries(subcat.services || {}).map(([key, service]) => (
                      <div
                        key={key}
                        className={`p-2 rounded-lg border ${
                          service.allow_emergency
                            ? 'bg-red-50 border-red-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span>{service.icon}</span>
                          <span className="text-sm font-medium truncate">{service.name}</span>
                        </div>
                        {service.allow_emergency && (
                          <Badge className="mt-1 bg-red-100 text-red-700 text-xs">
                            🆘 Emergency
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Verification Tab */}
        <TabsContent value="verification">
          <Card>
            <CardHeader>
              <CardTitle>Pending Provider Verifications</CardTitle>
            </CardHeader>
            <CardContent>
              {pendingProviders.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-gray-600">No pending verifications</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingProviders.map((provider) => (
                    <div
                      key={provider.id}
                      className="p-4 border rounded-lg bg-amber-50 border-amber-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{provider.name}</h3>
                          {provider.business_name && (
                            <p className="text-sm text-gray-600">{provider.business_name}</p>
                          )}
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge variant="outline">{provider.subcategory_slug}</Badge>
                            <Badge variant="outline">{provider.country}</Badge>
                            <Badge variant="outline">{provider.service_area}</Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-2">
                            Services: {provider.services_offered?.join(', ')}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Select
                            onValueChange={(level) =>
                              handleVerification(provider.id, 'approved', parseInt(level))
                            }
                          >
                            <SelectTrigger className="w-40 bg-green-100">
                              <SelectValue placeholder="Approve as..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">Level 1 (Basic)</SelectItem>
                              <SelectItem value="2">Level 2 (Verified)</SelectItem>
                              <SelectItem value="3">Level 3 (Trusted)</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleVerification(provider.id, 'rejected')}
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Emergency Services Tab */}
        <TabsContent value="emergency">
          <EmergencyServicesSection country={country} />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <CountrySettingsSection country={country} countryConfig={countryConfig} onUpdate={fetchData} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Emergency Services Section Component
function EmergencyServicesSection({ country }) {
  const [emergencyServices, setEmergencyServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEmergency = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/trades-services/emergency-services?country=${country}`);
        const data = await res.json();
        setEmergencyServices(data.emergency_services || []);
      } catch (error) {
        console.error('Failed to fetch emergency services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmergency();
  }, [country]);

  if (loading) {
    return <div className="text-center py-8">Loading emergency services...</div>;
  }

  // Group by subcategory
  const grouped = emergencyServices.reduce((acc, svc) => {
    if (!acc[svc.subcategory_name]) {
      acc[svc.subcategory_name] = [];
    }
    acc[svc.subcategory_name].push(svc);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="font-semibold text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Emergency Services ({emergencyServices.length} total)
        </h3>
        <p className="text-sm text-red-600 mt-1">
          These services can be marked as "Emergency" by providers, indicating 24/7 availability and urgent response capability.
        </p>
      </div>

      {Object.entries(grouped).map(([subcatName, services]) => (
        <Card key={subcatName}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{subcatName}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {services.map((svc) => (
                <div
                  key={svc.service_key}
                  className="p-3 bg-red-50 border border-red-200 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{svc.icon}</span>
                    <span className="font-medium">{svc.name}</span>
                  </div>
                  <Badge className="mt-2 bg-red-100 text-red-700">
                    🆘 Emergency Enabled
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Country Settings Section Component
function CountrySettingsSection({ country, countryConfig, onUpdate }) {
  const [saving, setSaving] = useState(false);

  if (!countryConfig) {
    return <div className="text-center py-8">Loading settings...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Country-Specific Settings: {countryConfig.country?.name}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Currency</Label>
                <Input value={countryConfig.country?.currency || ''} disabled />
              </div>
              <div>
                <Label>Currency Symbol</Label>
                <Input value={countryConfig.country?.currency_symbol || ''} disabled />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Enabled Subcategories</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {countryConfig.subcategories?.map((sub) => (
                  <div
                    key={sub.slug}
                    className="flex items-center justify-between p-2 border rounded"
                  >
                    <div className="flex items-center gap-2">
                      <span>{sub.subcategory_icon}</span>
                      <span className="text-sm">{sub.subcategory_name}</span>
                    </div>
                    <Switch checked={true} disabled />
                  </div>
                ))}
              </div>
            </div>

            {country === 'LK' && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-700 mb-2">Custom LK-only Subcategories</h4>
                <p className="text-sm text-blue-600 mb-3">
                  These subcategories are only available in Sri Lanka and are not part of the global configuration.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {countryConfig.subcategories?.filter(s => s.is_custom).map(sub => (
                    <div key={sub.slug} className="p-2 bg-white rounded border border-blue-200">
                      <div className="flex items-center gap-2">
                        <span>{sub.subcategory_icon}</span>
                        <span className="font-medium">{sub.subcategory_name}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {Object.keys(sub.services || {}).length} services
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Compliance Disclaimers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {countryConfig.subcategories?.filter(s => s.disclaimer).map(sub => (
              <div key={sub.slug} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <span>{sub.subcategory_icon}</span>
                  <span className="font-medium">{sub.subcategory_name}</span>
                </div>
                <p className="text-sm text-amber-700">{sub.disclaimer}</p>
              </div>
            ))}
            {!countryConfig.subcategories?.some(s => s.disclaimer) && (
              <p className="text-gray-500 text-center py-4">No disclaimers configured</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
